import { useEffect, useState } from 'react';
import { X, Loader2, RefreshCw, Copy, Check, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ModalShell } from '@/components/ModalShell';

import { useResetPassword } from '@/features/users/useUsers';
import { generatePassword } from '@/lib/password';
import { ApiError } from '@/lib/api-client';
import { toast } from '@/stores/toast.store';
import type { AdminUser } from '@/types/api';

interface ResetPasswordModalProps {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}

export function ResetPasswordModal({ open, user, onClose }: ResetPasswordModalProps): JSX.Element | null {
  const [password, setPassword] = useState('');
  const [pwCopied, setPwCopied] = useState(false);
  const reset = useResetPassword();

  useEffect(() => {
    if (!open) return;
    setPassword(generatePassword());
    setPwCopied(false);
    reset.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !reset.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, reset.isPending]);

  if (!open || !user) return null;

  const copyPassword = async (): Promise<void> => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setPwCopied(true);
      window.setTimeout(() => setPwCopied(false), 2000);
    } catch {
      toast.warning("Couldn't copy", 'Select the password manually instead.');
    }
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    reset.mutate(
      { id: user.id, newPassword: password },
      {
        onSuccess: () => {
          toast.success(
            'Password reset',
            `${user.fullName}'s sessions were revoked. Share the new password with them.`,
          );
          onClose();
        },
      },
    );
  };

  return (
    <ModalShell
      open={true}
      onClose={() => !reset.isPending && onClose()}
      ariaLabelledBy="rp-modal-title"
    >
      <header className="sticky top-0 bg-white border-b border-nav-card px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
        <h2 id="rp-modal-title" className="font-head text-[17px] font-bold text-nav-navy flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-nav-amberText" />
          Reset password
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={reset.isPending} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto">
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4 font-body text-nav-navy">
          <p className="text-sm text-nav-navy">
            Set a new password for <strong>{user.fullName}</strong> ({user.email}). Their active
            sessions will be revoked and they'll be required to change the password on first sign-in.
          </p>

          {reset.error && (
            <Alert variant="error" role="alert">
              <AlertTitle>Reset failed</AlertTitle>
              <AlertDescription>
                {reset.error instanceof ApiError
                  ? reset.error.message
                  : (reset.error as Error).message}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="rp-pw">New password</Label>
            <div className="flex gap-2">
              <Input
                id="rp-pw"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="min-h-[44px]"
              />
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => setPassword(generatePassword())}
                aria-label="Generate password"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={copyPassword}
                aria-label="Copy password"
              >
                {pwCopied ? <Check className="h-4 w-4 text-nav-sageMid" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-nav-slate">
              Share this with the user out-of-band. The audit log will record that you reset it.
            </p>
          </div>
        </form>
      </div>

      <footer className="sticky bottom-0 bg-white border-t border-nav-card px-6 py-4 flex justify-end gap-2 rounded-b-lg">
        <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>
          Cancel
        </Button>
        <Button variant="warning" onClick={onSubmit} disabled={reset.isPending}>
          {reset.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {reset.isPending ? 'Resetting…' : 'Reset password'}
        </Button>
      </footer>
    </ModalShell>
  );
}
